import { useEffect, useRef, useState } from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { type UserRef } from '@/api/client'
import { searchDirectory, resolveUser } from '@/auth/graph'
import { cn } from '@/lib/utils'

interface Props {
    id: string
    value: string
    onChange: (value: string) => void
}

// Async combobox: searches people server-side (api.searchUsers), stores the
// selected user id, and displays their name. Resolves id→name on mount.
export default function UserReferenceInput({ value, onChange }: Props) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [items, setItems] = useState<UserRef[]>([])
    const [loading, setLoading] = useState(false)
    const [displayName, setDisplayName] = useState('')

    useEffect(() => {
        let cancelled = false
        if (value) {
            resolveUser(value)
                .then((u) => { if (!cancelled) setDisplayName(u?.name || '') })
                .catch(() => {})
        } else {
            setDisplayName('')
        }
        return () => { cancelled = true }
    }, [value])

    const debRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
        if (debRef.current) clearTimeout(debRef.current)
        const term = query.trim()
        if (term.length < 2) {
            setItems([])
            setLoading(false)
            return
        }
        setLoading(true)
        debRef.current = setTimeout(() => {
            searchDirectory(term)
                .then((r) => setItems(r))
                .catch(() => setItems([]))
                .finally(() => setLoading(false))
        }, 250)
        return () => { if (debRef.current) clearTimeout(debRef.current) }
    }, [query])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('w-full max-w-md justify-between font-normal', !displayName && 'text-muted-foreground')}
                >
                    {displayName || 'Search for a person…'}
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Type a name or email…" value={query} onValueChange={setQuery} />
                    <CommandList>
                        {loading && (
                            <div className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                            </div>
                        )}
                        {!loading && query.trim().length < 2 && (
                            <div className="px-3 py-6 text-sm text-muted-foreground">Type at least 2 characters.</div>
                        )}
                        {!loading && query.trim().length >= 2 && items.length === 0 && (
                            <CommandEmpty>No people found.</CommandEmpty>
                        )}
                        {items.length > 0 && (
                            <CommandGroup>
                                {items.map((u) => (
                                    <CommandItem
                                        key={u.id}
                                        value={u.id}
                                        onSelect={() => {
                                            onChange(u.id)
                                            setDisplayName(u.name)
                                            setOpen(false)
                                            setQuery('')
                                        }}
                                    >
                                        <Check className={cn('h-4 w-4', value === u.id ? 'opacity-100' : 'opacity-0')} />
                                        <div className="flex flex-col">
                                            <span>{u.name}</span>
                                            {u.email && <span className="text-xs text-muted-foreground">{u.email}</span>}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
